import { EnumButtonName } from '@calm/evexia-analytics';
import { useAudio } from '@rhg/hooks/use-audio';
import playerMessages from '@rhg/lang/messages/player';
import {
    IconButton,
    IconButtonProps,
    SxProps,
    Theme,
} from '@rhg/libs/material';
import { Back15, SkipBack } from '@rhg/libs/material/icons';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

const PREVIOUS_RESTART_THRESHOLD_S = 5;

export interface PreviousButtonProps {
    /** True for playlist content: button skips to previous track instead of rewinding 15s. */
    isPlaylist?: boolean;
    onPreviousTrack?: () => void;
    sx?: SxProps<Theme>;
    size?: IconButtonProps['size'];
}

export function PreviousButton({
    isPlaylist = false,
    onPreviousTrack,
    sx,
    size,
}: PreviousButtonProps) {
    const { formatMessage } = useIntl();
    // Read the playhead imperatively at click time from the (low-frequency)
    // media element rather than subscribing to the ~4Hz timing — this button
    // never needs to re-render as time advances. EVE-12668.
    const {
        controls: { setTime },
        mediaEl,
    } = useAudio();

    const onRewind = useCallback(
        () => setTime(Math.max((mediaEl?.currentTime ?? 0) - 15, 0)),
        [setTime, mediaEl]
    );

    const onPrevious = useCallback(() => {
        if ((mediaEl?.currentTime ?? 0) > PREVIOUS_RESTART_THRESHOLD_S) {
            setTime(0);
            return;
        }
        onPreviousTrack?.();
    }, [onPreviousTrack, setTime, mediaEl]);

    return (
        <IconButton
            trackButton={
                isPlaylist
                    ? EnumButtonName.PreviousTrack
                    : EnumButtonName.RewindFewSeconds
            }
            aria-label={
                isPlaylist
                    ? formatMessage(playerMessages['player.previous_track'])
                    : formatMessage(playerMessages['player.backward_15'])
            }
            onClick={isPlaylist ? onPrevious : onRewind}
            size={size}
            sx={sx}
        >
            {isPlaylist ? <SkipBack /> : <Back15 />}
        </IconButton>
    );
}
